import { Spacing, Text, Title } from '@vkontakte/vkui';
import neuroTariffPromoDark from 'img/aiTariff/neuroTariffPromoDark.png';
import neuroTariffPromoLight from 'img/aiTariff/neuroTariffPromoLight.png';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { aiTariffAbVariantB } from 'reactApp/appHelpers/featuresHelpers/features/aiTariff';
import { trial256gbNeurofilterPromo } from 'reactApp/appHelpers/featuresHelpers/features/neurofilter';
import { cdnPackagesUrl } from 'reactApp/appHelpers/themeToolHelpers/default-values';
import type { IProps } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { renderCommonWhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog.helpers';
import { sendDwhModal, sendDwhRadar } from 'reactApp/modules/dwh/dwh.helpers';
import { getPaySource } from 'reactApp/modules/payment/payment.analytics';
import { openFastCheckout } from 'reactApp/modules/payment/payment.module';
import { productsController } from 'reactApp/modules/products/products.controller';
import { isCurrentDarkTheme } from 'reactApp/modules/theme/theme.selectors';
import { UserStorageActions, UserStorageSelectors } from 'reactApp/modules/user/userStorage';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { noopVoid } from 'reactApp/utils/helpers';

import styles from './NeuroPromoObject.css';

const smallStar = `${cdnPackagesUrl}/static/octavius/promo/neuro-tariff/icon_small.png`;
const bigStar = `${cdnPackagesUrl}/static/octavius/promo/neuro-tariff/icon_big.png`;

const name = 'mnz_buy_with_neiro';
const modalName = 'mnz_neiro_tariff_from_cloud_object';
const paySource = 'try_neiro_tariff_from_cloud_modal';
const PROMO_ID = 'neuro-promo-object-shown';

const renderPopup = (isDarkTheme = false, payClick = noopVoid) => {
    const data: IProps = {
        title: (
            <span>
                {aiTariffAbVariantB
                    ? 'Новый тариф с нейросетью в Облаке — Mail Space Нейро'
                    : 'Нейросеть в Облаке — с подпиской Mail Space'}
            </span>
        ),
        content: (
            <>
                <Spacing size={14} />
                <>
                    Найдёт дубли фото и подскажет, какие удалить, чтобы освободить
                    <br />
                    место. Фото с разными людьми разложит по папкам. А в Почте
                    <br />
                    соберёт важные письма и перескажет
                </>
                <Spacing size={24} />
            </>
        ),
        primaryButtonText: <span>Попробовать за&nbsp;1&nbsp;₽</span>,
        imageUrl: isDarkTheme ? neuroTariffPromoDark : neuroTariffPromoLight,
        onClick: () => {
            sendDwhModal({ name: modalName, action: 'click' });
            payClick();
        },
        onDismiss: () => {
            sendDwhModal({ name: modalName, action: 'close' });
        },
        onShow: () => {
            sendDwhModal({ name: modalName });
            productsController.updateProducts(trial256gbNeurofilterPromo);
        },
        dialogSize: 'large',
        buttonTheme: 'vk',
        closeAfterClick: true,
        contentClassName: styles.content,
        imageClassName: styles.image,
        imageWrapperClassName: styles.imageWrapper,
        closeIconClassName: styles.closeIcon,
        closeIconWrapperClassName: styles.closeIconWrapper,
        qaId: 'neuro-prm-pp',
    };

    return renderCommonWhatsNewDialog(data);
};

export const NeuroPromoObject: React.FC = () => {
    const dispatch = useDispatch();
    const [show, setShow] = useState(false);
    const isOverquota = useSelector(UserQuotaSelectors.isOverquota);
    const isDarkTheme = useSelector(isCurrentDarkTheme);
    const wasShownAndClosed = useSelector((state) => UserStorageSelectors.get(state, PROMO_ID)) || 0;
    const isViewerOpening = useSelector(ViewerSelectors.isViewerOpening);
    const doNotShow = isOverquota || wasShownAndClosed || isViewerOpening || !show;

    useEffect(() => {
        setTimeout(() => setShow(true), 250);
    }, []);

    useEffect(() => {
        if (doNotShow) {
            return;
        }

        sendDwhRadar({ action: 'show', t: 'object', name });
    }, [doNotShow]);

    const payClick = useCallback(async () => {
        dispatch(
            openFastCheckout({
                paySource: getPaySource(paySource),
                productId: trial256gbNeurofilterPromo,
                hideOtherTariffs: true,
                title: 'Подписка Mail Space с нейросетью на месяц бесплатно',
                featuresList: [
                    'Функции нейросети',
                    'Без рекламы в Почте и Облаке',
                    'Загрузка файлов до 100 ГБ в Облако',
                    'Отправка файлов до 100 ГБ в письмах',
                    'Добавление ещё 3 участников',
                    'Пароли для совместных папок в веб-версии',
                    'Приоритетная поддержка',
                ],
            })
        );
    }, []);

    const handleClick = useCallback(() => {
        sendDwhRadar({ action: 'click', t: 'object', name });

        dispatch(UserStorageActions.set(PROMO_ID, 1));

        renderPopup(isDarkTheme, payClick);
    }, [isDarkTheme, payClick]);

    if (doNotShow) {
        return null;
    }

    return (
        <div className={styles.root} onClick={handleClick} data-test-id="neuro-promo-object">
            <div className={styles.promo}>
                <div className={styles.stars}>
                    <img src={smallStar} className={styles.smallStar} />
                    <img src={bigStar} className={styles.bigStar} />
                </div>
                <Title className={styles.title}>нейро</Title>
                <Text className={styles.description}>Что это?</Text>
            </div>
        </div>
    );
};
